//@flow
import * as React from 'react';
import { Box, Stack, Text, Columns, Column, Flex } from '@locus-taxy/neo-ui';
import {
    neoUIColors,
    colorList,
    dashboardColors
} from 'common/enums/ColorEnum';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum,
    popOverFactory
} from 'common/components/core/popover/popOverFactory';
import { type TFunction } from 'react-i18next';

import BeatInfoTooltipContent from './BeatInfoTooltipContent';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import { type FpBeat } from 'common/models/internal/FpBeat';
import { FpMapColorByOptions } from './FpPlanMapOptions';

const TextWithPopover = popOverFactory({ Component: Text });

type FpTimelineLeftCardProps = {
    UnitConverter: UnitConversionHelper,
    beat: FpBeat,
    toggleBeatSelection: (beat: string) => void,
    index: number,
    isBeatSelected: boolean,
    theme: ThemeType,
    style: Object,
    mapOptions: Object,
    salesCategoryColorMap: Map<string, { color: string }>,
    riderIdColorMap: Map<string, { color: string }>,
    weekColorMap: Map<string, { color: string }>,
    weekDayColorMap: Map<string, { color: string }>,
    onScroll: () => void,
    visibleBeatColorMap: Map<string, { color: string }>,
    t: TFunction
};
export default class FpTimelineLeftCard extends React.PureComponent<FpTimelineLeftCardProps> {
    toggleBeat = () => {
        const { beat, toggleBeatSelection } = this.props;
        const { beatId } = beat;
        toggleBeatSelection(beatId);
    };
    render() {
        const {
            beat,
            index,
            isBeatSelected,
            theme,
            style,
            mapOptions,
            salesCategoryColorMap,
            onScroll,
            visibleBeatColorMap,
            UnitConverter,
            riderIdColorMap,
            weekColorMap,
            weekDayColorMap,
            t
        } = this.props;
        const [category] = beat.categories;
        let cardBgColor;
        if (isBeatSelected) {
            cardBgColor = visibleBeatColorMap.get(beat.beatId);
        }
        if (mapOptions.colorBy) {
            if (mapOptions.colorByType === FpMapColorByOptions.SalesCategory) {
                cardBgColor = salesCategoryColorMap.get(category);
            } else if (mapOptions.colorByType === FpMapColorByOptions.Riders) {
                cardBgColor =
                    riderIdColorMap.get(beat.currentSalesmanId) || 'grey.500';
            } else if (mapOptions.colorByType === FpMapColorByOptions.Week) {
                cardBgColor = weekColorMap.get(beat.beatDay.week) || 'grey.500';
            } else if (mapOptions.colorByType === FpMapColorByOptions.WeekDay) {
                cardBgColor =
                    weekDayColorMap.get(beat.beatDay.dayOfWeek) || 'grey.500';
            }
        }
        if (!isBeatSelected && !mapOptions.colorBy) {
            cardBgColor = 'grey.200';
        }

        return (
            <div
                key={beat.beatId}
                className={theme.listItemWrap}
                style={style}
                onScroll={onScroll}
            >
                <Box
                    height={'74px'}
                    p={4}
                    sx={{
                        border: `1px solid ${neoUIColors.grey200.code}`,
                        maxWidth: '300px',
                        background: isBeatSelected
                            ? 'white'
                            : dashboardColors.offWhite.code,
                        cursor: 'pointer'
                    }}
                    onClick={this.toggleBeat}
                >
                    <Columns align="center" alignY="center" space="small">
                        <Column width="content">
                            <Flex
                                backgroundColor={neoUIColors.grey700.code}
                                justifyContent="center"
                                alignItems="center"
                                sx={{
                                    width: '24px',
                                    height: '20px',
                                    borderRadius: '4px'
                                }}
                            >
                                <Text
                                    fontWeight={700}
                                    fontSize={1}
                                    color={colorList['white-two']}
                                >
                                    {index + 1}
                                </Text>
                            </Flex>
                        </Column>
                        <Column>
                            <Stack>
                                <Text
                                    color={neoUIColors.grey900.code}
                                    fontWeight={600}
                                    fontSize={1}
                                >
                                    {beat.currentSalesmanId}
                                </Text>
                                <Columns space="xSmall" alignY="center">
                                    <Column width="content">
                                        <Box
                                            bg={cardBgColor}
                                            p={1}
                                            sx={{
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <Text
                                                color={
                                                    mapOptions.colorBy ||
                                                    isBeatSelected
                                                        ? 'white'
                                                        : 'grey.600'
                                                }
                                                fontWeight={600}
                                                fontSize={1}
                                            >
                                                {' '}
                                                {beat.beatDay.week}
                                            </Text>
                                        </Box>
                                    </Column>
                                    <Column>
                                        <Text
                                            fontSize={0}
                                            fontWeight={400}
                                            color="grey.600"
                                        >
                                            {beat.beatDay.dayOfWeek}
                                        </Text>
                                    </Column>
                                    <Column width="content">
                                        <Box
                                            bg={cardBgColor}
                                            p={1}
                                            sx={{
                                                borderRadius: '4px'
                                            }}
                                        >
                                            <TextWithPopover
                                                popOverContent={
                                                    <BeatInfoTooltipContent
                                                        t={t}
                                                        UnitConverter={
                                                            UnitConverter
                                                        }
                                                        beat={beat}
                                                        theme={theme}
                                                    />
                                                }
                                                triggerPopoverOn={
                                                    PopoverTriggerEnum.Hover
                                                }
                                                preferPlace={
                                                    PopoverPlaceEnum.Right
                                                }
                                            >
                                                <Text
                                                    color={
                                                        mapOptions.colorBy ||
                                                        isBeatSelected
                                                            ? 'white'
                                                            : 'grey.600'
                                                    }
                                                >
                                                    {beat.beatId}
                                                </Text>
                                            </TextWithPopover>
                                        </Box>
                                    </Column>
                                </Columns>
                            </Stack>
                        </Column>
                    </Columns>
                </Box>
            </div>
        );
    }
}
